import React from "react";
import { Link } from "react-router-dom";
import { Printer, Layers, Gauge, Globe2, Handshake } from "lucide-react";
import TopBar from "../components/shop/TopBar";
import Logo from "../components/Logo";
import { OutreachEmail } from "../components/shop/OutreachEmail";
import { DIM, TESTER_IMG, CONTACT, ROADMAP, COMMERCIAL } from "../data/build";

const LINKS = [
  { to: "/shop", label: "Pool Shop" },
  { to: "/shop/build", label: "Build Spec" },
  { to: "/shop/interior", label: "Interior" },
  { to: "/shop/brief", label: "Brief / RFQ" },
  { to: "/software", label: "Software" },
  { to: "/home", label: "Home System" },
];

const POINTS = [
  { icon: Layers, t: "What it is", d: "A benchtop & portable pool-water tester: 4 continuous probes (Free Cl · pH · Salt/TDS · Temp) + 3 top-loaded optical test-strip slots (6-in-1 · Phosphate · Magnesium). No liquid reagents." },
  { icon: Gauge, t: "How it works", d: "Pour the sample, the unit lights the strips each pool needs, reads the full panel in ~60–90 s, flood-cleans itself, then syncs to our pool-management software as a branded report." },
  { icon: Globe2, t: "The opportunity", d: "Pool shops, pool-service technicians and home users across a worldwide, multi-billion-dollar industry — one ecosystem spanning shop, mobile, home automation and software." },
  { icon: Handshake, t: "The ask", d: "A product-build partner with a vested interest to create the first operational prototype, then scale to full robotic production for 100% quality control without compromise." },
];

export default function ShopTeaser() {
  const today = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-slate-100" data-testid="shop-teaser-page">
      <div className="no-print"><TopBar links={LINKS} /></div>

      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 bg-slate-900 text-white">
        <div className="mx-auto max-w-[900px] px-5 h-14 flex items-center justify-between">
          <span className="font-mono2 text-xs uppercase tracking-widest text-slate-300 hidden sm:inline">One-page teaser · quick first contact</span>
          <div className="flex items-center gap-2">
            <Link to="/shop/brief" data-testid="teaser-to-brief-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:bg-white/10 text-white text-sm font-semibold px-4 py-2 transition-colors">Full brief / RFQ</Link>
            <button onClick={() => window.print()} data-testid="teaser-print-btn" className="inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold px-5 py-2 transition-colors">
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Outreach email (screen only) */}
      <div className="no-print mx-auto max-w-[900px] px-4 md:px-6 pt-6">
        <OutreachEmail />
      </div>

      {/* The one printable page */}
      <div className="brief-doc mx-auto max-w-[900px] px-4 md:px-6 py-8">
        <section className="brief-page bg-white rounded-xl border border-slate-200 shadow-sm px-8 py-9 md:px-12 md:py-12">
          <div className="flex items-center justify-between">
            <Logo />
            <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">Confidential · one-page overview</span>
          </div>

          <div className="mt-8 grid md:grid-cols-[1.15fr_1fr] gap-8 items-center">
            <div>
              <p className="font-mono2 text-xs text-sky-600 uppercase tracking-widest">Partnership opportunity</p>
              <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 leading-[1.08]">
                Pool Shop Water Tester
              </h1>
              <p className="mt-3 text-slate-600">
                Seeking a product-build partner to prototype and manufacture a reagentless pool-water tester for a
                worldwide, multi-billion-dollar industry.
              </p>
              <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="flex justify-between border-b border-slate-100 py-1"><dt className="text-slate-500">Panel time</dt><dd className="font-medium text-slate-900">{DIM.panel}</dd></div>
                <div className="flex justify-between border-b border-slate-100 py-1"><dt className="text-slate-500">Reagents</dt><dd className="font-medium text-slate-900">None</dd></div>
                <div className="flex justify-between border-b border-slate-100 py-1"><dt className="text-slate-500">Probes</dt><dd className="font-medium text-slate-900">4 continuous</dd></div>
                <div className="flex justify-between border-b border-slate-100 py-1"><dt className="text-slate-500">Strips</dt><dd className="font-medium text-slate-900">3 optical slots</dd></div>
              </dl>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src={TESTER_IMG.front} alt="Pool Shop tester" className="w-full object-cover" />
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {POINTS.map(({ icon: Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-slate-200 p-4" data-testid={`teaser-point-${t.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="h-8 w-8 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center"><Icon className="h-4 w-4 text-sky-600" /></span>
                  <p className="font-display font-bold text-slate-900">{t}</p>
                </div>
                <p className="text-sm text-slate-600">{d}</p>
              </div>
            ))}
          </div>

          <div className="mt-6" data-testid="teaser-roadmap">
            <p className="font-mono2 text-[10px] uppercase tracking-widest text-sky-700 mb-2">A three-step build path — the bigger opportunity</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {ROADMAP.map((r) => (
                <div key={r.step} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2">
                    <span className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center font-mono2 text-xs font-bold shrink-0">{r.step}</span>
                    <p className="font-display font-bold text-slate-900 text-sm leading-tight">{r.name}</p>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">{r.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500" data-testid="teaser-commercial">
              {COMMERCIAL.rollout} <span className="text-slate-400">Quoting bands (not forecasts): 1 prototype → 5–10 pilot → ~100 initial → 1,000+ scale.</span>
            </p>
          </div>

          <div className="mt-8 rounded-2xl bg-slate-900 text-white p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-display font-bold">{CONTACT.name}</p>
              <p className="text-sm text-slate-300">{CONTACT.company} · {CONTACT.location}</p>
            </div>
            <div className="text-sm text-right">
              <p className="font-mono2 text-sky-300">{CONTACT.phone}</p>
              <p className="font-mono2 text-sky-300">{CONTACT.email}</p>
            </div>
          </div>

          <p className="mt-6 text-xs text-slate-400">Full Manufacturing Brief &amp; RFQ — spec, 24-part component schedule and engineering drawings — available on request. {CONTACT.docRef} · {today}</p>
        </section>
      </div>
    </div>
  );
}
